import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useState } from "react";

import { trackBookingConfirmationConversion } from "@/utils/Analytics/Conversion";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import type { buildBookingConfirmationAnalyticsData_booking$key } from "@/utils/Analytics/bookings/__generated__/buildBookingConfirmationAnalyticsData_booking.graphql";
import {
  buildBookingConfirmationAnalyticsData,
  readBookingConfirmationAnalyticsData,
} from "@/utils/Analytics/bookings/buildBookingConfirmationAnalyticsData";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import {
  WhooshMutationError,
  useMutationWithError,
} from "../../../../../../utils/hooks/useMutationWithError";
import { bookingSummaryAnalyticsDataAtom } from "../atoms/analytics/bookingSummaryAnalyticsDataAtom";
import type { promoCodeAnalytics_booking$key } from "../components/BookingCredits/components/BookingPromoCode/__generated__/promoCodeAnalytics_booking.graphql";
import { getAppliedPromoCodeAnalyticsData } from "../components/BookingCredits/components/BookingPromoCode/promoCodeAnalytics";
import {
  ConfirmDraftBookingInput,
  useConfirmDraftBookingMutation,
} from "./__generated__/useConfirmDraftBookingMutation.graphql";

export const useConfirmDraftBooking = (
  membershipTypeId: string,
  bookingType: "STANDARD" | "LESSON",
  multipleSlotRequestGroupCount = 1
) => {
  const [requestErrors, setRequestErrors] = useState<WhooshMutationError[]>([]);
  const [confirmDraftBooking, isConfirming] =
    useMutationWithError<useConfirmDraftBookingMutation>(
      useConfirmDraftBookingMutationString
    );

  const onCompleted = useDraftBookingConfirmationCompleted();
  const handleConfirmDraftBooking = (
    input: ConfirmDraftBookingInput,
    successCallback?: () => void,
    isRequestBooking?: boolean,
    errorCallback?: (error: Error) => void
  ) => {
    setRequestErrors([]);
    confirmDraftBooking({
      variables: {
        input,
        membershipTypeId,
        bookingType,
      },
      onCompleted: (res) =>
        onCompleted(
          res.responsePayload?.success,
          res.responsePayload?.booking,
          successCallback,
          isRequestBooking,
          multipleSlotRequestGroupCount
        ),
      onCompletedErrorCustom: (errors) => {
        if (isRequestBooking && multipleSlotRequestGroupCount > 1) {
          setRequestErrors(errors);
        }
        errorCallback?.(
          new Error(errors[0]?.message || t`Unable to confirm booking.`)
        );
        return errors;
      },
      onPayloadError: (errors) => {
        errorCallback?.(
          new Error(errors[0]?.message || t`Unable to confirm booking.`)
        );
      },
      onErrorCustom: errorCallback,
    });
  };

  return [handleConfirmDraftBooking, isConfirming, requestErrors] as const;
};

export const useDraftBookingConfirmationCompleted = () => {
  const { showToast } = useToast();

  const analyticsData = useAtomValue(bookingSummaryAnalyticsDataAtom);
  const trackEvent = useTrackEvent();

  return (
    success: boolean | undefined,
    booking:
      | (buildBookingConfirmationAnalyticsData_booking$key &
          promoCodeAnalytics_booking$key)
      | null
      | undefined,
    successCallback?: () => void,
    isRequestBooking?: boolean,
    multipleSlotRequestGroupCount = 1
  ) => {
    const confirmedBooking = readBookingConfirmationAnalyticsData(booking);
    trackBookingConfirmationConversion({
      success,
      eventName: "booking_confirmed",
      analyticsData,
      bookings: confirmedBooking ? [confirmedBooking] : [],
    });
    trackEvent(
      "BookingDetail.DraftBookingConfirmed",
      buildBookingConfirmationAnalyticsData(
        analyticsData,
        confirmedBooking ? [confirmedBooking] : []
      ) ?? {}
    );
    if (success && isRequestBooking && multipleSlotRequestGroupCount > 1) {
      trackEvent("MultipleSlotRequest.SubmissionSucceeded", {
        groupCount: multipleSlotRequestGroupCount,
      });
    }
    const promoCodeAnalytics = getAppliedPromoCodeAnalyticsData(booking);
    if (promoCodeAnalytics) {
      trackEvent("PromoCodeRedeemed", promoCodeAnalytics);
    }
    if (!isRequestBooking) {
      showToast({
        title: t`Booking Confirmed`,
        semanticStyle: "success",
        toastId: "confirm-draft-booking-success",
      });
    }
    if (successCallback) {
      successCallback();
    }
  };
};

const useConfirmDraftBookingMutationString = graphql`
  mutation useConfirmDraftBookingMutation(
    $input: ConfirmDraftBookingInput!
    $membershipTypeId: ID!
    $bookingType: BookingType!
  ) {
    responsePayload: confirmDraftBooking(input: $input) {
      booking {
        id
        ...promoCodeAnalytics_booking
        ...buildBookingConfirmationAnalyticsData_booking
        ...ChargesSummary_booking @arguments(patronLoyalty: false)
        ...PaymentCardEntryModals_booking
        ...BookingPayment_Booking @arguments(patronLoyalty: false)
        ...InviteOthersBox_Booking
        ...BookingInfo_Booking
        ...RoundList_Booking
        ...BookingPolicy_Booking
        ...BookingPaymentMethodSelect_booking
        ...BookingReservation_Booking @arguments(patronLoyalty: false)
        ...BookingHeaderFragment_booking
        ...CancelBookingButton_Booking
        startSlot {
          id
          ...TeeSheetAgendaConsistency_timeSlot
        }
      }
      errors {
        __typename
        field
        code
        message
        ... on MultipleSlotRequestValidationError {
          bookingIndex
          roundIndex
          playerId
        }
      }
      success
    }
  }
`;
